import {
	UseFormSetValue,
	FieldValues,
	useFormContext,
	UseFormWatch,
	UseFormGetFieldState,
	Control
} from 'react-hook-form';
import { useLocationFilter } from '../hooks/useLocationFilter';
import DynamicSearchSelect from './form-control/form-builder/form-builder-element/dynamic-select';
import { useTranslation } from 'react-i18next';
import { memo, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';


export interface LocationSearchSelectProps<
	T extends FieldValues = FieldValues
> {
	selectedCompany?: string[] | string | number | null | undefined;
	control?: Control<T>;
	setValue?: UseFormSetValue<T>;
	fieldArrayName: string;
	locationFieldName?: string;
	label?: string;
	placeholder?: string;
	resetValue?: any;
	filterKey?: string;
	[key: string]: any;
}

export interface LocationFilterType {
	[key: string]: string[] | string | number;
}

export const LocationSearchSelect = memo(
	<T extends FieldValues = FieldValues>({
		selectedCompany,
		control: propControl,
		setValue: propSetValue,
		fieldArrayName,
		locationFieldName = 'location_id',
		label,
		placeholder,
		resetValue = undefined,
		filterKey = '&company_id.in',
		filterField = 'company_id',
		...dynamicSearchSelectProps
	}: LocationSearchSelectProps<T>): JSX.Element => {
		const formContext = useFormContext();
		const {
			control: contextControl,
			watch: contextWatch,
			setValue: contextSetValue,
			getFieldState: contextGetFieldState
		} = formContext || {};

		const controlToUse = propControl || contextControl;
		const setValueToUse = propSetValue || contextSetValue;
		const watchToUse: UseFormWatch<T> = contextWatch || (() => undefined);
		const getFieldStateToUse: UseFormGetFieldState<T> =
			contextGetFieldState || (() => ({}));

		const filterFieldValue = watchToUse(`${fieldArrayName}.${filterField}`);
		const isDirty = getFieldStateToUse(
			`${fieldArrayName}.${filterField}`
		)?.isDirty;

		const prevFilterFieldValue = useRef<any>();

		const { t } = useTranslation();

		const isValueChanged = useMemo(() => {
			return filterFieldValue && prevFilterFieldValue.current
				? !_.isEqual(prevFilterFieldValue.current, filterFieldValue)
				: false;
		}, [filterFieldValue]);


		useEffect(() => {
			if (filterFieldValue) {
				prevFilterFieldValue.current = filterFieldValue;
			}
		}, [filterFieldValue]);

		const locationFilter: LocationFilterType = useLocationFilter({
			selectedCompany: filterFieldValue || selectedCompany,
			setValue: setValueToUse,
			fieldArrayName,
			locationFieldName,
			resetValue,
			filterKey,
			isDirty: isValueChanged || isDirty
		});

		// Now safely do the conditional render
		if (!controlToUse) {
			console.warn('LocationSearchSelect: No form control available.');
			return <div>Location Search Select</div>;
		}

		return (
			<DynamicSearchSelect
				label={t(label) || t('crm.salesOrder.fields.location_label')}
				apiType='location'
				addType='location'
				enable_footer={true}
				name={locationFieldName}
				placeholder={
					t(placeholder) || t('crm.salesOrder.fields.location_placeholder')
				}
				fieldArrayName={fieldArrayName}
				formControl={controlToUse}
				customeFilter={locationFilter}
				{...dynamicSearchSelectProps}
			/>
		);
	}
);

LocationSearchSelect.defaultProps = {
	locationFieldName: 'location_id',
	resetValue: undefined,
	filterKey: '&company_id.in'
};

export default LocationSearchSelect